import {cn} from '../lib/utils'
import {Icon} from './Icon'
import {cva, type VariantProps} from 'class-variance-authority'
import type {HTMLAttributes} from 'react'
import {useEffect, useId, useState} from 'react'

const accordionButtonVariants = cva(
  'flex w-full items-center justify-between overflow-hidden text-left transition-colors',
  {
    variants: {
      disabled: {
        true: 'cursor-default text-text-disabled',
        false: 'cursor-pointer hover:text-text-inverse-hover',
      },
      size: {
        s: 'py-8 text-sm leading-sm font-light',
        m: 'py-16 text-base leading-base font-medium',
        l: 'py-24 text-lg leading-lg font-medium',
        'lg-compact': 'py-24 text-sm leading-sm font-medium',
      },
    },
    defaultVariants: {
      disabled: false,
      size: 's',
    },
  },
)

const accordionIconWrapperVariants = cva('relative size-20 shrink-0 transition-transform', {
  variants: {
    expanded: {
      true: 'rotate-180',
      false: '',
    },
  },
  defaultVariants: {
    expanded: false,
  },
})

const accordionIconVariants = cva('inset-0 absolute text-current transition-opacity', {
  variants: {
    visible: {
      true: 'opacity-100',
      false: 'opacity-0',
    },
  },
  defaultVariants: {
    visible: true,
  },
})

const accordionContentWrapperVariants = cva('transition-grid ease-in-out grid duration-300', {
  variants: {
    expanded: {
      true: 'grid-rows-[1fr]',
      false: 'grid-rows-[0fr]',
    },
  },
  defaultVariants: {
    expanded: false,
  },
})

const accordionContentVariants = cva('', {
  variants: {
    size: {
      s: 'pb-8',
      m: 'pb-16',
      l: 'pb-24',
      'lg-compact': 'pb-24',
    },
    expanded: {
      true: 'visible',
      false: 'invisible delay-300',
    },
  },
  defaultVariants: {
    size: 's',
    expanded: false,
  },
})

interface AccordionProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'onClick' | 'title'>,
    VariantProps<typeof accordionButtonVariants> {
  open?: boolean
  defaultOpen?: boolean
  title?: React.ReactNode
  className?: string
  headerClassName?: string
  contentClassName?: string
  disabled?: boolean
  size?: 's' | 'm' | 'l' | 'lg-compact'
  /** Element used to render the title. Defaults to `span`; set a heading (e.g. `h2`) for semantic document structure. */
  titleTag?: 'span' | 'h2' | 'h3' | 'h4'
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  /** Additional props for the header button */
  headerProps?: Record<string, unknown>
  /** Skip the expand/collapse animation. */
  skipAnimation?: boolean
  /**
   * Test ID for Playwright selectors. Applied to the container; the header
   * button and content region derive `${dataTestId}-button` / `${dataTestId}-content`
   * (a `data-testid` passed via headerProps still wins on the button).
   */
  dataTestId?: string
}

/**
 * Figma: https://www.figma.com/design/qS60CfMVFGs07l2G3yfhsB/%F0%9F%92%A0-Components?node-id=10194-10522&m=dev
 */
export const Accordion = ({
  className,
  headerClassName,
  contentClassName,
  open,
  defaultOpen,
  children,
  disabled,
  title,
  titleTag: TitleTag = 'span',
  size,
  onClick,
  headerProps,
  skipAnimation,
  dataTestId,
}: AccordionProps) => {
  const [expanded, setExpanded] = useState(!!defaultOpen)
  const uniqueId = useId()

  useEffect(() => {
    if (open !== undefined) setExpanded(!!open)
  }, [open])

  return (
    <div
      className={cn('w-full border-b border-border-tertiary', className)}
      data-testid={dataTestId}
    >
      <TitleTag className="contents">
        <button
          type="button"
          className={accordionButtonVariants({disabled, size, className: headerClassName})}
          onClick={(event) => {
            if (open === undefined) {
              setExpanded(!expanded)
            } else {
              onClick?.(event)
            }
          }}
          id={`accordion-button-${uniqueId}`}
          disabled={disabled}
          aria-expanded={expanded}
          aria-controls={`accordion-content-${uniqueId}`}
          data-testid={dataTestId ? `${dataTestId}-button` : undefined}
          {...headerProps}
        >
          <span>{title}</span>
          <div className={accordionIconWrapperVariants({expanded})} aria-hidden="true">
            <Icon name="plus" className={accordionIconVariants({visible: !expanded})} />
            <Icon name="minus" className={accordionIconVariants({visible: expanded})} />
          </div>
        </button>
      </TitleTag>
      <div
        id={`accordion-content-${uniqueId}`}
        className={cn(accordionContentWrapperVariants({expanded}), skipAnimation && 'duration-0')}
        aria-hidden={!expanded}
        role="region"
        aria-labelledby={`accordion-button-${uniqueId}`}
        data-testid={dataTestId ? `${dataTestId}-content` : undefined}
      >
        <div className="overflow-hidden">
          <div className={accordionContentVariants({size, expanded, className: contentClassName})}>
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

Accordion.displayName = 'Accordion'
